<template>
  <div class="pane-content">
    <el-row type="flex" class="mt-2 mb-1">
      <h5 class="section-header">Aloware Voice Intelligence Entity Mapper</h5>
    </el-row>
    <el-row>
      <p class="mt-3">
        Enable to sync contact properties dynamically during every call without needing to manually update them in your CRM.
      </p>
      <p v-if="contactAvaMappingStatus">
        Properties selected in the AloAi Voice Analytics (AVA) Entity dropdown will map to the properties selected in the {{ integrationNameWithCasing }} Contact Property dropdown. These will update automatically based on the call transcription. The properties will map during each call where the AVA Entity is detected.
      </p>
    </el-row>
    <el-row>
      <el-switch v-model="contactAvaMappingStatus" active-color="#00a344" @change="handleContactAvaMappingStatusChange"></el-switch>
    </el-row>
    <template v-if="contactAvaMappingStatus">
      <div class="p-3 container mt-4 mt-1 mb-4">
        <i class="el-icon-warning-outline mr-1"></i>
        <span>
        If PII (Personally Identifiable Information) filtering is enabled in your <a :href="transcriptionSettingsUrl" target="_blank" class="link">transcription settings</a>, certain entities containing sensitive personal information will not sync even if mapped.
      </span>
      </div>
      <sync-ava-contact-mapper
        :model="model"
        :integration-name="integrationName"
        :integration-name-with-casing="integrationNameWithCasing"
        :static-name="staticName"
      />
    </template>
  </div>
</template>

<script>
import SyncAvaContactMapper from './shared/sync-ava-contact-mapper.vue'
import _ from 'lodash'

export default {
  components: {
    SyncAvaContactMapper,
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    integrationName: {
      type: String,
      required: true
    },
    integrationNameWithCasing: {
      type: String,
      required: true
    },
    statics: {
      type: Object,
      required: false
    },
    defaultMapperSettings: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      contactAvaMappingStatus: false
    }
  },
  computed: {
    staticName() {
      return this.statics.name || 'Aloware'
    },
    transcriptionSettingsUrl() {
      return `${window.location.origin}/account?tab=transcription-settings`
    }
  },
  methods: {
    getContactEntityUpdateOperation() {
      // mapper_settings.ava is an array, find the entity.update & contact.update operation
      if (!Array.isArray(this.model.settings.mapper_settings.ava)) {
        return null
      }

      return this.model.settings.mapper_settings.ava.find(
        op => op.internal_value === 'entity.update' && op.crm_value === 'contact.update'
      )
    },
    handleContactAvaMappingStatusChange(newVal) {
      const entityUpdateOperation = this.getContactEntityUpdateOperation()

      if (!entityUpdateOperation) {
        return
      }

      // Update the enabled status
      entityUpdateOperation.enabled = newVal

      // If disabling, clear mappings and show notification
      if (!newVal) {
        // Clear the mappings in the store state (will be saved by settings.vue saveSettings())
        this.$store.commit('avaEntityMapping/SET_AVA_ENTITY_CONTACT_MAPPINGS', [])

        this.$notify({
          title: 'AVA Entity Mapper Disabled',
          message: 'All AVA entity mappings will be removed when you save the settings.',
          type: 'warning',
          duration: 4000
        })
      }
    }
  },
  created() {
    // Initialize mapper_settings.ava if it doesn't exist (for accounts created before AVA feature)
    if (!this.model.settings.mapper_settings.ava && this.defaultMapperSettings?.ava) {
      this.$set(this.model.settings.mapper_settings, 'ava', _.cloneDeep(this.defaultMapperSettings.ava))
    }
    
    const entityUpdateOperation = this.getContactEntityUpdateOperation()
    this.contactAvaMappingStatus = entityUpdateOperation?.enabled ?? false
  }
}
</script>

<style scoped>
.pane-content {
  display: flex;
  flex-direction: column;
  padding-left: 8px;
  padding-right: 8px;
}

.section-header {
  margin: 0 12px 0 0;
  font-size: 20px;
}

.container {
  border: 1px solid grey;
  border-radius: 5px;
  width: 100%;
  max-width: 100%;
}

.link {
  color: #0051C1;
  font-weight: bold;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.p-3 {
  padding: 1rem;
}
</style>
